import { resetLoanCache } from "../lib/loanCache";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, loginHref } from "../lib/api";
import { qk, useAuthProviders, useDevUsers } from "../lib/queries";
import { Button, Spinner, Wordmark } from "../components/ui";
import { roleLabel } from "../lib/roles";

const AUTH_ERRORS: Record<string, string> = {
  domain_not_allowed: "That Microsoft account isn't allowed here. Sign in with your LendSure work account.",
  account_conflict: "An admin must verify and bind this account's Microsoft tenant object ID before sign-in.",
  expired: "The sign-in took too long. Please try again.",
  idp_error: "Microsoft couldn't complete the sign-in. Please try again.",
  config: "Sign-in isn't configured correctly. Contact IT.",
};

export function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-navy-950 to-navy-800 px-4 py-10">
      <svg className="pointer-events-none absolute bottom-0 left-0 h-56 w-56 opacity-90" viewBox="0 0 100 100" aria-hidden>
        <path d="M0 100 L0 30 L70 100 Z" fill="#65AF00" />
      </svg>
      <div className="mb-6">
        <Wordmark />
      </div>
      <main className="relative w-full max-w-md rounded-[10px] bg-surface p-7 shadow-[var(--shadow-3)]">{children}</main>
      <p className="relative mt-6 text-[12px] text-white/75">Internal use only · LendSure Mortgage Corp.</p>
    </div>
  );
}

export function SignInPage() {
  const qc = useQueryClient();
  const dev = useDevUsers();
  const providers = useAuthProviders();
  const extraProviders = (providers.data ?? []).filter((p) => !p.primary);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const params = new URLSearchParams(window.location.search);
  const authError = params.get("auth_error");
  const returnTo = (() => {
    const p = new URLSearchParams(window.location.search);
    p.delete("auth_error");
    const qs = p.toString();
    return window.location.pathname + (qs ? `?${qs}` : "");
  })();

  const devLogin = async (email: string) => {
    setBusy(email);
    setErr(null);
    try {
      await api("/api/dev/login", { method: "POST", body: { email } });
      if (authError) window.history.replaceState(null, "", returnTo);
      await qc.invalidateQueries({ queryKey: qk.me });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Frame>
      <div className="eyebrow text-navy-700 dark:text-navy-300">Pipeline</div>
      <h1 className="font-display mt-1 text-[22px] font-semibold text-ink">Sign in to your pipeline</h1>
      {authError ? (
        <div role="alert" className="mt-4 rounded-md bg-danger-bg px-3 py-2 text-[13px] text-danger">
          {AUTH_ERRORS[authError] ?? `Sign-in failed (${authError}). Please try again.`}
        </div>
      ) : null}
      <a
        href={loginHref(returnTo)}
        className="focus-ring mt-5 flex h-11 w-full items-center justify-center gap-3 rounded-md bg-primary font-medium text-on-primary hover:bg-primary-hover"
      >
        <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden>
          <rect x="1" y="1" width="9" height="9" fill="#F25022" />
          <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
          <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
          <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
        </svg>
        Sign in with Microsoft (LendSure)
      </a>
      <p className="mt-2 text-[13px] text-ink-2">Use your LendSure work account.</p>
      {extraProviders.length ? (
        <p className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-ink-3">
          {extraProviders.map((p) => (
            <a
              key={p.tenant_id}
              href={loginHref(returnTo, p.tenant_id)}
              className="focus-ring rounded-sm underline-offset-2 hover:text-ink-2 hover:underline"
              title={p.label}
            >
              {extraProviders.length > 1 ? `Administrator sign-in (${p.label})` : "Administrator sign-in"}
            </a>
          ))}
        </p>
      ) : null}

      {dev.isSuccess && dev.data.length ? (
        <section className="mt-6 border-t border-subtle pt-4" aria-labelledby="dev-users">
          <h2 id="dev-users" className="eyebrow text-warning">
            Dev mode · sign in as
          </h2>
          {err ? <div className="mt-2 text-[13px] text-danger">{err}</div> : null}
          <ul className="mt-2 max-h-72 space-y-1 overflow-y-auto">
            {dev.data.map((u) => (
              <li key={u.email}>
                <button
                  className="focus-ring flex w-full items-center justify-between gap-2 rounded-md border border-subtle px-3 py-2 text-left hover:bg-hover"
                  onClick={() => void devLogin(u.email)}
                  disabled={!!busy}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-ink">{u.display_name}</span>
                    <span className="block truncate text-[12px] text-ink-3">{u.email}</span>
                  </span>
                  <span className="shrink-0 text-[12px] text-ink-2">
                    {busy === u.email ? <Spinner /> : `${roleLabel(u.role)}${u.team_name ? ` · ${u.team_name}` : ""}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </Frame>
  );
}

async function logout(qc: ReturnType<typeof useQueryClient>) {
  try {
    await api("/api/auth/logout", { method: "POST" });
  } finally {
    resetLoanCache();
    qc.clear();
    window.location.assign("/");
  }
}

export function PendingPage({ kind, email }: { kind: "pending_approval" | "disabled"; email?: string }) {
  const qc = useQueryClient();
  return (
    <Frame>
      <div className="eyebrow text-navy-700 dark:text-navy-300">Pipeline</div>
      {kind === "pending_approval" ? (
        <>
          <h1 className="font-display mt-1 text-[22px] font-semibold text-ink">You're almost in</h1>
          <p className="mt-3 text-ink-2">
            You're signed in{email ? ` as ${email}` : ""}, and an admin needs to approve your account before you can see loans. We've added you to their list — this usually
            takes less than a day.
          </p>
        </>
      ) : (
        <>
          <h1 className="font-display mt-1 text-[22px] font-semibold text-ink">Your access is turned off</h1>
          <p className="mt-3 text-ink-2">Your Pipeline account is disabled. If you think this is a mistake, contact your manager or an admin.</p>
        </>
      )}
      <div className="mt-6 flex gap-2">
        <Button variant="primary" onClick={() => void qc.invalidateQueries({ queryKey: qk.me })}>
          Check again
        </Button>
        <Button onClick={() => void logout(qc)}>Sign out</Button>
      </div>
    </Frame>
  );
}

/** Signed-in LendSure member with no role yet: a friendly holding screen. */
export function WelcomePage({ name, email }: { name: string; email?: string }) {
  const qc = useQueryClient();
  return (
    <Frame>
      <div className="eyebrow text-navy-700 dark:text-navy-300">Pipeline</div>
      <h1 className="font-display mt-1 text-[22px] font-semibold text-ink">Welcome to LendSure Pipeline</h1>
      <p className="mt-3 text-ink-2">
        You're signed in as <span className="font-medium text-ink">{name}</span>
        {email && email !== name ? <span className="text-ink-3"> ({email})</span> : null}. Your RM or an admin will give you access.
      </p>
      <div className="mt-6 flex gap-2">
        <Button variant="primary" onClick={() => void qc.invalidateQueries({ queryKey: qk.me })}>
          Check again
        </Button>
        <Button onClick={() => void logout(qc)}>Sign out</Button>
      </div>
    </Frame>
  );
}

export { logout };
